/**
 * Field Conditions — pure suncalc computations.
 *
 * All functions are synchronous. No network calls. Works offline.
 * Uses suncalc 2.0.0 to compute solar and lunar context
 * relevant to fly fishing in Southern Maine.
 *
 * Domain defaults: Portland, ME area (lat 43.661, lon -70.255)
 * Used when GPS position is unavailable.
 */

import * as SunCalc from "suncalc";

/** Default coordinates: Portland, ME — centroid of Southern Maine coverage */
export const DEFAULT_LAT = 43.661;
export const DEFAULT_LON = -70.255;

export interface SolarContext {
  sunrise: Date;
  sunset: Date;
  goldenHourMorningEnd: Date; // end of morning golden hour
  goldenHourEveningStart: Date; // start of evening golden hour
  isDaytime: boolean;
  inMorningGoldenHour: boolean;
  inEveningGoldenHour: boolean;
  sunriseStr: string;
  sunsetStr: string;
  goldenMorningStr: string;
  goldenEveningStr: string;
}

export interface MoonContext {
  phase: number; // 0=new, 0.25=first quarter, 0.5=full, 0.75=last quarter
  fraction: number; // 0–1 illumination
  phaseName: string;
  moonrise: Date | null;
  moonset: Date | null;
  moonriseStr: string;
  moonsetStr: string;
}

export interface SolunarPeriod {
  label: "Major" | "Minor";
  start: Date;
  end: Date;
  str: string;
  isActive: boolean;
}

/** Format a Date as "h:mm AM/PM" */
function fmtTime(d: Date | null | undefined): string {
  if (!d || isNaN(d.getTime())) return "—";
  return d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", hour12: true });
}

/** Moon phase 0–1 → human name */
function phaseName(phase: number): string {
  if (phase < 0.03 || phase > 0.97) return "New Moon";
  if (phase < 0.22) return "Waxing Crescent";
  if (phase < 0.28) return "First Quarter";
  if (phase < 0.47) return "Waxing Gibbous";
  if (phase < 0.53) return "Full Moon";
  if (phase < 0.72) return "Waning Gibbous";
  if (phase < 0.78) return "Last Quarter";
  return "Waning Crescent";
}

export function getSolarContext(lat = DEFAULT_LAT, lon = DEFAULT_LON, now = new Date()): SolarContext {
  const times = SunCalc.getTimes(now, lat, lon);

  // Null-safe: suncalc returns null in polar regions. Portland ME never hits this,
  // but TypeScript requires explicit handling. Epoch/far-future fallbacks short-circuit safely.
  const sunrise = times.sunrise ?? new Date(0);
  const sunset = times.sunset ?? new Date(8640000000000000);
  const goldenHourEnd = times.goldenHourEnd ?? new Date(0);
  const goldenHour = times.goldenHour ?? new Date(8640000000000000);

  const inMorningGoldenHour = now >= sunrise && now <= goldenHourEnd;
  const inEveningGoldenHour = now >= goldenHour && now <= sunset;

  return {
    sunrise,
    sunset,
    goldenHourMorningEnd: goldenHourEnd,
    goldenHourEveningStart: goldenHour,
    isDaytime: now >= sunrise && now <= sunset,
    inMorningGoldenHour,
    inEveningGoldenHour,
    sunriseStr: fmtTime(sunrise),
    sunsetStr: fmtTime(sunset),
    goldenMorningStr: `${fmtTime(sunrise)}–${fmtTime(goldenHourEnd)}`,
    goldenEveningStr: `${fmtTime(goldenHour)}–${fmtTime(sunset)}`,
  };
}

export function getMoonContext(lat = DEFAULT_LAT, lon = DEFAULT_LON, now = new Date()): MoonContext {
  const illum = SunCalc.getMoonIllumination(now);
  const moonTimes = SunCalc.getMoonTimes(now, lat, lon);

  return {
    phase: illum.phase,
    fraction: illum.fraction,
    phaseName: phaseName(illum.phase),
    moonrise: moonTimes.rise ?? null,
    moonset: moonTimes.set ?? null,
    moonriseStr: fmtTime(moonTimes.rise),
    moonsetStr: fmtTime(moonTimes.set),
  };
}

/**
 * Compute solunar feeding period windows.
 *
 * Classic solunar theory:
 *   Minor periods: moonrise ±30 min, moonset ±30 min
 *   Major periods: moon overhead (~transit, ≈ midpoint of rise→set) ±1 hr
 *                  moon underfoot (~anti-transit) ±1 hr
 *
 * Transit is approximated as the midpoint between moonrise and moonset.
 * This is accurate to within ~15 minutes for this use case.
 */
export function getSolunarPeriods(lat = DEFAULT_LAT, lon = DEFAULT_LON, now = new Date()): SolunarPeriod[] {
  const moonTimes = SunCalc.getMoonTimes(now, lat, lon);
  const periods: SolunarPeriod[] = [];

  const MINOR_HALF = 30 * 60 * 1000; // 30 min
  const MAJOR_HALF = 60 * 60 * 1000; // 60 min

  function addPeriod(center: Date, label: "Major" | "Minor") {
    const half = label === "Major" ? MAJOR_HALF : MINOR_HALF;
    const start = new Date(center.getTime() - half);
    const end = new Date(center.getTime() + half);
    periods.push({
      label,
      start,
      end,
      str: `${fmtTime(start)}–${fmtTime(end)}`,
      isActive: now >= start && now <= end,
    });
  }

  if (moonTimes.rise) addPeriod(moonTimes.rise, "Minor");
  if (moonTimes.set) addPeriod(moonTimes.set, "Minor");

  if (moonTimes.rise && moonTimes.set) {
    // Major period 1: transit (overhead) — midpoint of rise→set
    const transitMs = (moonTimes.rise.getTime() + moonTimes.set.getTime()) / 2;
    addPeriod(new Date(transitMs), "Major");

    // Major period 2: anti-transit (underfoot) — 12h offset from transit
    addPeriod(new Date(transitMs + 12 * 60 * 60 * 1000), "Major");
  }

  return periods.sort((a, b) => a.start.getTime() - b.start.getTime());
}

/**
 * Build the full field conditions string for `useCopilotReadable` injection.
 * The agent receives this on every message — no need for it to ask.
 */
export function buildFieldConditionsContext(lat = DEFAULT_LAT, lon = DEFAULT_LON): string {
  const now = new Date();
  const solar = getSolarContext(lat, lon, now);
  const moon = getMoonContext(lat, lon, now);
  const solunar = getSolunarPeriods(lat, lon, now);

  const activeperiods = solunar.filter((p) => p.isActive);
  const nextPeriod = solunar.find((p) => p.start > now);

  const lines = [
    `Date/Time: ${now.toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" })} at ${fmtTime(now)}`,
    `Sunrise: ${solar.sunriseStr} | Sunset: ${solar.sunsetStr}`,
    `Golden hour (morning): ${solar.goldenMorningStr}${solar.inMorningGoldenHour ? " ← ACTIVE NOW" : ""}`,
    `Golden hour (evening): ${solar.goldenEveningStr}${solar.inEveningGoldenHour ? " ← ACTIVE NOW" : ""}`,
    `Moon: ${moon.phaseName} (${Math.round(moon.fraction * 100)}% illuminated)`,
    `Moonrise: ${moon.moonriseStr} | Moonset: ${moon.moonsetStr}`,
    `Solunar periods today: ${solunar.map((p) => `${p.label} ${p.str}`).join(", ")}`,
    activeperiods.length > 0
      ? `⚡ ACTIVE SOLUNAR PERIOD: ${activeperiods[0].label} period — fish actively now`
      : nextPeriod
        ? `Next solunar period: ${nextPeriod.label} ${nextPeriod.str}`
        : "No more solunar periods today",
  ];

  return lines.join("\n");
}
